import { useEffect, useRef, useState } from "react";
import { useSocket } from "../state/SocketContext";

interface Props {
  className?: string;
  collapsedDefault?: boolean;
}

export function Chat({ className, collapsedDefault = true }: Props) {
  const { chat, sendChat, room, socketId } = useSocket();
  const [collapsed, setCollapsed] = useState(collapsedDefault);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!collapsed) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, collapsed]);

  if (!room) return null;

  const submit = async () => {
    const msg = value.trim();
    if (!msg) return;
    const res = await sendChat(msg);
    if (!res.ok) {
      setError(res.error === "RATE_LIMITED" ? "Slow down, soldier." : "Send failed.");
      setTimeout(() => setError(null), 1500);
      return;
    }
    setValue("");
  };

  return (
    <div
      className={[
        "fixed bottom-4 right-4 z-30 flex w-[min(92vw,360px)] flex-col rounded-2xl border border-white/15",
        "bg-cap-deep/90 backdrop-blur-md shadow-2xl",
        className ?? "",
      ].join(" ")}
    >
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center justify-between rounded-t-2xl border-b border-white/10 px-4 py-2 text-sm font-display uppercase tracking-widest text-white/90"
      >
        <span>Team Comms</span>
        <span className="text-cap-gold">{collapsed ? "▲" : "▼"}</span>
      </button>
      {!collapsed && (
        <>
          <div className="flex max-h-64 min-h-[8rem] flex-col gap-2 overflow-y-auto p-3">
            {chat.length === 0 && (
              <div className="m-auto text-center text-xs text-white/40">
                No messages yet.
              </div>
            )}
            {chat.map((m, i) => {
              const self = m.from === socketId;
              return (
                <div
                  key={`${m.at}-${i}`}
                  className={`flex flex-col ${self ? "items-end" : "items-start"}`}
                >
                  <span className="mb-0.5 text-[10px] uppercase tracking-widest text-white/40">
                    {self ? "You" : m.name}
                  </span>
                  <span
                    className={[
                      "max-w-[80%] break-words rounded-2xl px-3 py-2 text-sm",
                      self
                        ? "rounded-br-sm bg-cap-blue text-white"
                        : "rounded-bl-sm bg-white/10 text-white",
                    ].join(" ")}
                  >
                    {m.message}
                  </span>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          {error && (
            <div className="px-3 pb-1 text-xs text-cap-gold">{error}</div>
          )}
          <form
            className="flex gap-2 border-t border-white/10 p-2"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <input
              value={value}
              onChange={(e) => setValue(e.target.value.slice(0, 200))}
              placeholder="Send to your ally…"
              className="flex-1 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-cap-gold focus:outline-none"
            />
            <button
              type="submit"
              className="rounded-full bg-cap-red px-4 py-2 text-xs font-display uppercase tracking-wider text-white active:scale-95"
            >
              Send
            </button>
          </form>
        </>
      )}
    </div>
  );
}
